import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// svc-04 `/v1/admin/email-settings*` — global SES email-config singleton
// (rule §D.9). Mirrors the Postman "Email Settings (svc-04 · admin)" folder.
// All three routes require `canManageUsers`. GET never 404s; POST /test
// always returns HTTP 200 — read `data.ok` for success/failure.

export interface EmailSettings {
  id: number;
  enabled: boolean;
  fromEmail: string;
  fromName: string | null;
  replyToEmail: string | null;
  testRecipient: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

// Read-only infrastructure config (env-sourced; never writable).
export interface EmailSettingsEnv {
  defaultFromAddress: string;
}

export interface EmailSettingsEnvelope {
  configured: boolean;
  settings: EmailSettings | null;
  env: EmailSettingsEnv;
}

export interface UpsertEmailSettingsBody {
  enabled: boolean;
  fromEmail: string;
  fromName?: string | null;
  replyToEmail?: string | null;
  testRecipient?: string | null;
}

export interface SendTestSuccess {
  ok: true;
  messageId: string;
}

export interface SendTestFailure {
  ok: false;
  stage: "ses-config" | "ses-send";
  code: string;
  message: string;
}

export type SendTestResult = SendTestSuccess | SendTestFailure;

const BASE = "/v1/admin/email-settings";

/** GET /v1/admin/email-settings — singleton fetch; never 404. */
export async function getEmailSettings(): Promise<EmailSettingsEnvelope> {
  const res = await Api.get(BASE);
  return unwrap<EmailSettingsEnvelope>(res.data);
}

/** PUT /v1/admin/email-settings — strict; unknown fields → 400. */
export async function upsertEmailSettings(
  body: UpsertEmailSettingsBody,
): Promise<EmailSettingsEnvelope> {
  const res = await Api.put(BASE, body);
  return unwrap<EmailSettingsEnvelope>(res.data);
}

/** POST /v1/admin/email-settings/test — always 200; read `ok`. */
export async function sendTestEmail(toEmail: string): Promise<SendTestResult> {
  const res = await Api.post(`${BASE}/test`, { toEmail });
  return unwrap<SendTestResult>(res.data);
}
